import { Currency } from "@redotech/money/currencies";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";
import {
  CheckoutImageBlockJustification,
  CheckoutOptimizationWidgetType,
  CheckoutReviewsProvider,
  lowProfileManualProductSourceSchema,
  ShippingOptionRenderTarget,
  ThresholdManualProductSourceSchema,
  UpsellProductDiscountSchema,
} from "./widget";

const baseWidgetSchema = z.object({
  /**
   * Defined `id` means the widget is existing and needs to be updated.
   * Undefined `id` means the widget is new and needs to be created
   */
  id: zExt.objectId().optional(),
  name: z.string(),
  /**
   * @deprecated Widget existence in collection indicates it's enabled
   */
  enabled: z.boolean().optional(),
});

const baseStandardWidgetSchema = baseWidgetSchema.extend({
  /**
   * When true, this widget applies to all checkouts globally (not tied to a specific checkout experience)
   */
  enabledGlobally: z.boolean().optional(),
  // Free-form render target. Known/default values come from
  // `PurchaseCheckoutBlockRenderTarget`, but merchants can place widgets at
  // arbitrary string targets (e.g. "block1").
  target: z.string(),
});

export const checkoutWidgetsSchema = z.object({
  [CheckoutOptimizationWidgetType.THRESHOLD_UPSELL]: baseStandardWidgetSchema
    .extend({
      type: z.literal(CheckoutOptimizationWidgetType.THRESHOLD_UPSELL),
      thresholdAmount: z.number(),
      currency: createEnum(Currency),
      gteThresholdText: z.string(),
      ltThresholdText: z.string(),
      addToCartButtonColor: z.string(),
      addToCartButtonTextColor: z.string(),
      progressBarColor: z.string(),
      excludedProductTags: z.array(z.string()),
      fallbackProductTags: z.array(z.string()),
      manualProductSources: z.array(ThresholdManualProductSourceSchema),
      defaultProductDiscount: UpsellProductDiscountSchema.optional(),
    })
    .optional(),
  [CheckoutOptimizationWidgetType.LOW_PROFILE_PRODUCT_UPSELL]:
    baseStandardWidgetSchema
      .extend({
        type: z.literal(
          CheckoutOptimizationWidgetType.LOW_PROFILE_PRODUCT_UPSELL,
        ),
        addToCartButtonColor: z.string(),
        addToCartButtonTextColor: z.string(),
        excludedProductTags: z.array(z.string()),
        fallbackProductTags: z.array(z.string()),
        manualProductSources: z.array(lowProfileManualProductSourceSchema),
      })
      .optional(),
  [CheckoutOptimizationWidgetType.FULFILLMENT_CUTOFF]: baseStandardWidgetSchema
    .extend({
      type: z.literal(CheckoutOptimizationWidgetType.FULFILLMENT_CUTOFF),
      text: z.string(),
      backgroundColor: z.string(),
      iconColor: z.string(),
    })
    .optional(),
  [CheckoutOptimizationWidgetType.CONTENT_BLOCK]: baseStandardWidgetSchema
    .extend({
      type: z.literal(CheckoutOptimizationWidgetType.CONTENT_BLOCK),
      sections: z.array(
        z.object({
          _id: z.string(),
          title: z.string().nullish(),
          description: z.string().nullish(),
          imageUrl: z.string().nullish(),
        }),
      ),
    })
    .optional(),
  [CheckoutOptimizationWidgetType.IMAGE_BLOCK]: baseStandardWidgetSchema
    .extend({
      type: z.literal(CheckoutOptimizationWidgetType.IMAGE_BLOCK),
      imageUrl: z.string().nullish(),
      imageWidthPx: z.coerce.string(),
      imageJustification: createEnum(CheckoutImageBlockJustification),
    })
    .optional(),
  [CheckoutOptimizationWidgetType.SHIPPING_OPTION_CONTENT]: baseWidgetSchema
    .extend({
      target: createEnum(ShippingOptionRenderTarget),
      type: z.literal(CheckoutOptimizationWidgetType.SHIPPING_OPTION_CONTENT),
      title: z.string(),
      description: z.string(),
      imageUrl: z.string().nullish(),
      backgroundColor: z.string(),
    })
    .optional(),
  [CheckoutOptimizationWidgetType.SHIPPING_OPTION_IMAGE]: baseWidgetSchema
    .extend({
      target: createEnum(ShippingOptionRenderTarget),
      type: z.literal(CheckoutOptimizationWidgetType.SHIPPING_OPTION_IMAGE),
      imageUrl: z.string().nullish(),
      imageWidth: z.string(),
      imageJustification: createEnum(CheckoutImageBlockJustification),
    })
    .optional(),
  [CheckoutOptimizationWidgetType.FAILED_DISCOUNT_RESCUE]:
    baseStandardWidgetSchema
      .extend({
        type: z.literal(CheckoutOptimizationWidgetType.FAILED_DISCOUNT_RESCUE),
        discountId: zExt.objectId().optional(),
        text: z.string(),
        buttonText: z.string(),
      })
      .optional(),
  [CheckoutOptimizationWidgetType.PRODUCT_REVIEWS]: baseStandardWidgetSchema
    .extend({
      type: z.literal(CheckoutOptimizationWidgetType.PRODUCT_REVIEWS),
      provider: createEnum(CheckoutReviewsProvider),
      minRating: z.number().int().min(1).max(5),
      display: z.object({
        showProductName: z.boolean(),
        showReviewTitle: z.boolean(),
        showReviewBody: z.boolean(),
        showReviewerName: z.boolean(),
        showVerifiedBadge: z.boolean(),
      }),
    })
    .optional(),
});

/**
 * The global state store for the checkout widgets builder.
 */
const checkoutWidgetsBuilderStateSchema = z.object({
  selectedWidget: createEnum(CheckoutOptimizationWidgetType),
  checkoutWidgets: checkoutWidgetsSchema,
});
export type CheckoutWidgetsBuilderState = z.infer<
  typeof checkoutWidgetsBuilderStateSchema
>;
